import { Users, SlidersHorizontal, ShieldCheck, LayoutTemplate, Building2, BarChart3, Languages, KeyRound, History, Settings2, Wallet, Bell, Palette } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useAuth, can } from "../lib/auth";
import { useLang } from "../lib/i18n";
import { EmployeesPanel } from "./admin/EmployeesPanel";
import { FieldsPanel } from "./admin/FieldsPanel";
import { RolesPanel } from "./admin/RolesPanel";
import { TemplatesPanel } from "./admin/TemplatesPanel";
import { OrganizationPanel } from "./admin/OrganizationPanel";
import { InsightsPanel } from "./admin/InsightsPanel";
import { TranslationsPanel } from "./admin/TranslationsPanel";
import { SecurityPanel } from "./admin/SecurityPanel";
import { ActivityPanel } from "./admin/ActivityPanel";
import { WalletSettingsPanel } from "./admin/WalletSettingsPanel";
import { NotificationsPanel } from "./admin/NotificationsPanel";
import { ProfileDesign } from "./admin/ProfileDesign";

const TABS = [
  { key: "employees", label: "Employees", icon: Users, perm: "employees.view", Panel: EmployeesPanel },
  { key: "insights", label: "Insights", icon: BarChart3, perm: "reports.view", Panel: InsightsPanel },
  { key: "cards", label: "Fields & templates", icon: SlidersHorizontal, children: [
    { key: "fields", label: "Form fields", icon: SlidersHorizontal, perm: "settings.fields", Panel: FieldsPanel },
    { key: "templates", label: "Templates", icon: LayoutTemplate, perm: "templates.manage", Panel: TemplatesPanel },
  ] },
  { key: "admin", label: "System settings", icon: Settings2, children: [
    { key: "profile", label: "Profile design", icon: Palette, perm: "settings.branding", Panel: ProfileDesign },
    { key: "roles", label: "Roles & permissions", icon: ShieldCheck, perm: "roles.manage", Panel: RolesPanel },
    { key: "translations", label: "Translations", icon: Languages, perm: "translations.manage", Panel: TranslationsPanel },
    { key: "activity", label: "Activity log", icon: History, perm: "audit.view", Panel: ActivityPanel },
    { key: "wallet", label: "Wallet settings", icon: Wallet, perm: "wallet.manage", Panel: WalletSettingsPanel },
    { key: "notifications", label: "Notifications", icon: Bell, perm: "settings.notifications", Panel: NotificationsPanel },
  ] },
  { key: "organization", label: "Organization", icon: Building2, perm: "settings.branding", Panel: OrganizationPanel },
  { key: "security", label: "Security", icon: KeyRound, ownerOnly: true, Panel: SecurityPanel },
];

const allowed = (user) => (tab) => (tab.ownerOnly ? user.is_owner : can(user, tab.perm));
const visibleTabs = (user) => TABS.map((tab) => (tab.children ? { ...tab, children: tab.children.filter(allowed(user)) } : tab))
  .filter((tab) => (tab.children ? tab.children.length : allowed(user)(tab)));

const TabBar = ({ tabs, small }) => {
  const { t } = useLang();
  return (
    <TabsList className={`h-auto flex-wrap justify-start gap-1 rounded-2xl p-1 ${small ? "bg-slate-100" : "bg-white shadow-sm ring-1 ring-slate-200/80"}`}>
      {tabs.map(({ key, label, icon: Icon }) => (
        <TabsTrigger key={key} value={key} data-testid={`admin-tab-${key}`}
          className={`gap-2 rounded-xl font-semibold data-[state=active]:bg-slate-900 data-[state=active]:text-white ${small ? "px-3 py-1.5 text-xs" : "px-3 py-2 text-sm"}`}>
          <Icon className="h-4 w-4" /> {t(label)}
        </TabsTrigger>
      ))}
    </TabsList>
  );
};

const TabPanel = ({ tab }) => {
  const { lang } = useLang();
  if (!tab.children) return <tab.Panel />;
  if (tab.children.length === 1) { const Only = tab.children[0].Panel; return <Only />; }
  return (
    <Tabs dir={lang === "ar" ? "rtl" : "ltr"} defaultValue={tab.children[0].key} className="space-y-4">
      <TabBar tabs={tab.children} small />
      {tab.children.map(({ key, Panel }) => <TabsContent key={key} value={key} className="mt-0"><Panel /></TabsContent>)}
    </Tabs>
  );
};

export default function AdminPage() {
  const { user } = useAuth();
  const { t, lang } = useLang();
  const tabs = visibleTabs(user);
  if (!tabs.length) return null;

  return (
    <div className="space-y-4" data-testid="admin-page">
      <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">{t("Settings")}</h1>
      <Tabs dir={lang === "ar" ? "rtl" : "ltr"} defaultValue={tabs[0].key} className="space-y-5">
        <TabBar tabs={tabs} />
        {tabs.map((tab) => <TabsContent key={tab.key} value={tab.key} className="mt-0"><TabPanel tab={tab} /></TabsContent>)}
      </Tabs>
    </div>
  );
}
